import { useState, type FormEvent } from "react";
import { Button, Input, FormField, Heading, Text } from "@/components/ds";
import styles from "./OnboardingStep.module.css";

interface OnboardingSiteStepProps {
  readonly onComplete: () => void;
  readonly onSkip: () => void;
}

export function OnboardingSiteStep({ onComplete, onSkip }: OnboardingSiteStepProps) {
  const [name, setName] = useState("");
  const [postcode, setPostcode] = useState("");
  const [nameError, setNameError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const trimmed = name.trim();
    if (!trimmed || trimmed.length < 2) {
      setNameError("Site name must be at least 2 characters");
      return;
    }

    setLoading(true);
    try {
      const body: Record<string, unknown> = { name: trimmed };
      const trimmedPostcode = postcode.trim();
      if (trimmedPostcode) {
        body.postcode = trimmedPostcode;
      }

      const response = await fetch("/api/sites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        setNameError(data.error?.fields?.name ?? data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      onComplete();
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.header}>
        <Heading level={1} size="h3">Your first cleaning site</Heading>
        <Text size="sm" color="muted">
          Great start! Now, where does your team clean? You can add more sites later.
        </Text>
      </div>

      <FormField label="Site name" htmlFor="onboarding-site-name" error={nameError}>
        <Input
          name="siteName"
          id="onboarding-site-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameError("");
          }}
          placeholder="e.g. Acme Corp HQ"
          error={!!nameError}
        />
      </FormField>

      <FormField label="Postcode" htmlFor="onboarding-postcode" hint="We'll use this for GPS check-in">
        <Input
          name="postcode"
          id="onboarding-postcode"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          placeholder="e.g. SW1A 1AA"
        />
      </FormField>

      <div className={styles.actions}>
        <Button type="submit" fullWidth disabled={loading}>
          {loading ? "Adding..." : "Add site"}
        </Button>
        <button type="button" className={styles.skipButton} onClick={onSkip} disabled={loading}>
          I'll do this later
        </button>
      </div>
    </form>
  );
}
